import React from 'react'
import { generatePath, useParams } from 'react-router-dom'
import { Button, Modal, notification } from 'antd'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'

import { useApi } from 'admin/lib/Api'
import { FormSectionTitle, FormSectionWrapper } from 'admin/lib/Forms'

import * as ADMIN_ROUTES from 'admin/routes'

type PossibleDuplicateRecordControlsProps = {
  details: {
    possibleDuplicateRecordIds: string[]
  }
}

export function PossibleDuplicateRecordControls({
  details
}: PossibleDuplicateRecordControlsProps) {
  const { accountId, moduleName, projectId, recordId } = useParams<{
    accountId: string
    projectId: string
    moduleName: string
    recordId: string
  }>()

  const api = useApi()

  const handleConfirmPossibleDuplicate = async (
    possibleDuplicateRecordId: string
  ) => {
    if (api) {
      const [, status, error] = await api.confirmPossibleDuplicate(
        accountId ? accountId : '',
        projectId ? projectId : '',
        moduleName ? moduleName : '',
        recordId ? recordId : '',
        possibleDuplicateRecordId ? possibleDuplicateRecordId : ''
      )

      if (error) {
        console.error(error)
        return Modal.error({
          title: '"Confirm as duplicate" Failed',
          content: String(error),
          centered: true
        })
      } else if ([200, 201].includes(status)) {
        notification.success({
          message: 'Record confirmed as duplicate!',
          description:
            'This record has been confirmed as a duplicate and has been set to have "Void" status. You may close this tab now.',
          placement: 'top'
        })
        setTimeout(() => {
          window.location.reload()
        }, 5000)
      }
    }
    return
  }

  const handleRejectPossibleDuplicate = async (
    possibleDuplicateRecordId: string
  ) => {
    if (api) {
      const [, status, error] = await api.rejectPossibleDuplicate(
        accountId ? accountId : '',
        projectId ? projectId : '',
        moduleName ? moduleName : '',
        recordId ? recordId : '',
        possibleDuplicateRecordId ? possibleDuplicateRecordId : ''
      )

      if (error) {
        console.error(error)
        return Modal.error({
          title: '"Reject as duplicate" Failed',
          content: String(error),
          centered: true
        })
      } else if ([200, 201].includes(status)) {
        window.location.reload()
      }
    }
    return
  }

  return (
    <PossibleDuplicateWrapper className="possible-duplicate-wrapper warning-no-icon">
      <FormSectionWrapper>
        <FormSectionTitle level={5} style={{ marginTop: 0 }}>
          Possible Duplicate Record
          {details.possibleDuplicateRecordIds.length !== 1 ? 's' : ''} Detected
        </FormSectionTitle>
      </FormSectionWrapper>
      {details.possibleDuplicateRecordIds.map(
        (possibleDuplicateRecordId, index) => (
          <PossibleDuplicateContainer key={possibleDuplicateRecordId}>
            <IndexContainer>{index + 1})</IndexContainer>
            <ButtonContainer>
              <Button
                icon={<Icons.EyeOutlined />}
                href={
                  possibleDuplicateRecordId
                    ? generatePath(ADMIN_ROUTES.RECORD.EDIT, {
                        accountId: accountId,
                        projectId: projectId,
                        moduleName: moduleName,
                        recordId: possibleDuplicateRecordId
                      })
                    : '#'
                }
                target="_blank"
              >
                View
              </Button>
              <Button
                type="primary"
                icon={<Icons.FlagOutlined />}
                onClick={() =>
                  handleRejectPossibleDuplicate(possibleDuplicateRecordId)
                }
              >
                Not Duplicate
              </Button>
              <Button
                type="primary"
                danger
                icon={<Icons.FlagOutlined />}
                onClick={() =>
                  handleConfirmPossibleDuplicate(possibleDuplicateRecordId)
                }
              >
                Is Duplicate
              </Button>
            </ButtonContainer>
          </PossibleDuplicateContainer>
        )
      )}
    </PossibleDuplicateWrapper>
  )
}

const PossibleDuplicateWrapper = styled.div`
  margin-bottom: 0.75rem !important;
`
const PossibleDuplicateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.25rem;
`
const IndexContainer = styled.div`
  min-width: 1rem;
  text-align: right;
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 0.375rem;

  & .ant-btn > .anticon + span,
  .ant-btn > span + .anticon {
    margin-left: 0;
  }
`
